import * as React from "react";
import { cn } from "@/lib/utils";
import { GradientText, SectionLabel } from "./GradientText";
import { SpotlightCard, SpotlightCardContent } from "./SpotlightCard";

interface Step {
  step: number;
  title: string;
  description: string;
}

interface HowItWorksProps {
  title: string;
  subtitle?: string;
  steps: Step[];
}

export function HowItWorks({ title, subtitle, steps }: HowItWorksProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "text-center max-w-3xl mx-auto mb-16 lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <SectionLabel className="mb-6">Process</SectionLabel>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4">
            <GradientText>{title}</GradientText>
          </h2>

          {subtitle && (
            <p className="text-muted-foreground text-base lg:text-lg">{subtitle}</p>
          )}
        </div>

        {/* Steps */}
        <div className="relative max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((step, index) => (
              <SpotlightCard
                key={step.step}
                className={cn(
                  "group",
                  "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                )}
                style={{
                  transitionDelay: isVisible ? `${(index + 1) * 150}ms` : "0ms",
                }}
              >
                <SpotlightCardContent className="h-full p-6">
                  {/* Step number - rounded square */}
                  <div className="mb-6">
                    <div
                      className={cn(
                        "relative w-14 h-14 rounded-2xl",
                        "bg-accent/10",
                        "border border-accent/20",
                        "flex items-center justify-center",
                        "text-accent font-semibold text-xl",
                        "transition-all duration-300",
                        "group-hover:border-accent/40"
                      )}
                    >
                      {step.step}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-semibold text-foreground mb-3 transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {step.description}
                  </p>
                </SpotlightCardContent>
              </SpotlightCard>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
